export const colors = {
  text: {
    primary: '#111827',
    secondary: '#6B7280',
  },
  surface: {
    page: '#F6F7F9',
    muted: '#F1F3F6',
    overlay: 'rgba(64,64,64,0.06)',
  },
  border: {
    default: '#E5E7EB',
    hover: '#D1D5DB',
    light: '#F0F1F3',
  },
  brand: {
    dark: '#102126',
    darkHover: '#17343A',
    onDark: '#FFFFFF',
    onDarkMuted: 'rgba(255,255,255,0.72)',
    onDarkFaint: 'rgba(255,255,255,0.45)',
    onDarkBorder: 'rgba(255,255,255,0.08)',
  },
  accent: {
    /* Brand blue — #0d3cfc. Previously a drifted #2F6BFF; unified so every
     * token group resolves to the single brand value. */
    blue: '#0d3cfc',
    blueHover: '#0b34d6',
    blueTint: '#E6EAFF',
    blueGlow: 'rgba(13,60,252,0.2)',
    orange: '#FA4E1D',
    orangeTint: 'rgba(250,78,29,0.08)',
    cyan: '#0d3cfc',
    cyanTint: 'rgba(13,60,252,0.08)',
  },
  header: {
    frost: 'rgba(246,247,249,0.80)',
    borderBottom: 'rgba(20,20,20,0.08)',
    scrollShadow: '0 6px 16px rgba(0,0,0,0.06)',
    navHover: 'rgba(20,20,20,0.06)',
  },
  status: {
    success: '#10B981',
    successLight: '#ECFDF5',
    danger: '#EF4444',
    dangerLight: '#FEF2F2',
    warning: '#D97706',
    warningLight: '#FFFBEB',
  },
  platform: {
    /* WeFixTrades brand accent — #0d3cfc (matches effortel.a400 / Stripe brand). */
    accent: '#0d3cfc',
    accentDark: '#0b34d6',
    accentLight: '#4f6dfd',
    accentLighter: '#E6EAFF',
    accentTint: 'rgba(13,60,252,0.06)',
    accentGlow: 'rgba(13,60,252,0.18)',
    pageBg: '#F6F7F9',
    pageBgSubtle: '#F1F3F6',
    heading: '#111827',
    body: '#374151',
    muted: '#6B7280',
    subtle: '#9CA3AF',
    border: '#E5E7EB',
    borderLight: '#F3F4F6',
    borderHover: '#D1D5DB',
    surface: '#FFFFFF',
    surfaceRaised: '#FAFBFC',
    surfaceHover: '#F8F9FB',
  },
  widget: {
    defaultAccent: '#0d3cfc',
    heading: '#111827',
    body: '#374151',
    muted: '#6B7280',
    subtle: '#9CA3AF',
    border: '#E5E7EB',
    borderLight: '#F1F5F9',
    borderHover: '#D1D5DB',
    surface: '#FFFFFF',
    surfaceRaised: '#FAFBFC',
    background: '#F6F7F9',
  },
  chart: {
    c1: '#0d3cfc',
    c2: '#0d3cfc',
    c3: '#FA4E1D',
    c4: '#D97706',
    c5: '#EF4444',
  },
  effortel: {
    n100: '#F5FCFF',
    n200: '#E4EDF1',
    n300: '#D5E1E7',
    n400: '#B1C5CE',
    n500: '#92A6B0',
    n600: '#5F6F77',
    n700: '#394247',
    n800: '#22282A',
    n900: '#171818',
    /* Accent ramp — formerly teal/cyan, swapped to primary blue
     * (#0d3cfc / #0b34d6 hover). Naming kept ("a100…a700") to avoid
     * a rename storm; semantic is now "blue accent" not "cyan". */
    a100: '#E6EAFF',
    a200: '#0b34d6',
    a400: '#0d3cfc',
    a500: '#0d3cfc',
    a700: '#0b34d6',
  },
  /* QuoteQuick dashboard design lock — canonical surface palette for the
   * builder wizard. See docs/quotequick-dashboard-design-lock.md.
   * Assembled into a usable theme by theme/dashboardTheme.ts. */
  dashboard: {
    canvas: '#A2B6BF',
    panel: '#E4EDF1',
    card: '#FFFFFF',
    cardMuted: '#F4F6F8',
    cardHover: '#F7F9FB',
    navActiveBg: '#22282A',
    navActiveIcon: '#0d3cfc',
    navIcon: '#6B7886',
    navIconHover: '#3A434B',
    accent: '#0d3cfc',
    accentDark: '#0b34d6',
    accentLight: '#4f6dfd',
    accentLighter: '#E6EAFF',
    accentTint: 'rgba(13,60,252,0.08)',
    heading: '#22282A',
    body: '#4A5560',
    muted: '#7E8A94',
    subtle: '#A2AEB7',
    border: '#D8E0E6',
    borderLight: '#E8EDF1',
    badgeBlueBg: '#D9E2FF',
    badgeBlueText: '#0b34d6',
    badgeGreenBg: '#BEEFD3',
    badgeGreenText: '#1B7A3D',
    badgeRedBg: '#F6D8DA',
    badgeRedText: '#B23A45',
    badgeAmberBg: '#FBE6C8',
    badgeAmberText: '#9A6512',
  },
} as const;

export const typography = {
  fontFamily: 'Satoshi, Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSans: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSerif: 'Georgia, serif',
  fontMono: 'Menlo, monospace',
  h1: { fontSize: '56px', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.03em', color: colors.effortel.n300 },
  h2: { fontSize: '40px', fontWeight: 650, lineHeight: 1.12, letterSpacing: '-0.02em', color: colors.effortel.n300 },
  h3: { fontSize: '28px', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em', color: colors.effortel.n300 },
  body: { fontSize: '16px', fontWeight: 400, lineHeight: 1.55, color: colors.effortel.n400 },
  small: { fontSize: '14px', fontWeight: 400, lineHeight: 1.45, color: colors.effortel.n400 },
  button: { fontSize: '15px', fontWeight: 500, lineHeight: 1.1, textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
  caption: { fontSize: '13px', fontWeight: 500, lineHeight: 1.4, color: colors.effortel.n400 },
  label: { fontSize: '12px', fontWeight: 700, lineHeight: 1.4, letterSpacing: '0.04em', textTransform: 'uppercase' as const },
} as const;

export const radius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
} as const;

export const shadows = {
  xs: '0 1px 2px rgba(0,0,0,0.04)',
  sm: '0 2px 6px rgba(0,0,0,0.05)',
  md: '0 6px 16px rgba(0,0,0,0.06)',
  lg: '0 12px 30px rgba(0,0,0,0.08)',
  xl: '0 16px 40px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.04)',
  card: '0 10px 20px #33314833',
  cardHover: '0 20px 36px #3331484d',
  button: '0 4px 10px rgba(0,0,0,0.08)',
  buttonHover: '0 6px 16px rgba(0,0,0,0.1)',
  focus: '0 0 0 3px rgba(13,60,252,0.25)',
  frost: '0 6px 16px rgba(0,0,0,0.06)',
  mega: '0 16px 50px rgba(0,0,0,0.10)',
} as const;

export const transitions = {
  fast: 'all 0.15s ease-out',
  normal: 'all 0.2s ease',
  slow: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

export const mkt = {
  bg: '#181D1F',
  surface: colors.effortel.n700,
  surfaceAlt: '#2E3638',
  text: colors.effortel.n300,
  textMuted: colors.effortel.n400,
  /* Faint/meta text on dark (timestamps, chat labels, disclaimers, hero meta
   * lines). The old n600 #5F6F77 read ~3.3:1 on the dark surfaces it sits on
   * (2.7:1 on sectionLight) — fails WCAG AA + the locked CONTRAST rule. Raised
   * to a legible cool-grey that clears 4.5:1 on every dark surface in use:
   * 6.44 on bg #181D1F, 6.85 on bgBase #161616, 5.32 on sectionLight #242D30,
   * 4.50 on sectionLighter #2E393C. Still visibly a step below textMuted
   * (n400, ~9.5:1) so hierarchy is preserved. */
  textFaint: '#96A1A6',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.04)',
  /* Accent: primary blue (#0d3cfc), hover dark blue (#0b34d6).
   * Glow tint kept low-opacity for ambient gradients ONLY — do not use
   * for hover halos. Hover affordance is a white border (see uses below). */
  accent: colors.effortel.a400,
  accentHover: colors.effortel.a200,
  accentDark: colors.effortel.a700,
  /* Accent used AS TEXT on dark surfaces. Brand blue #0d3cfc is a FILL
   * colour — as text on near-black it reads ~2.5:1 (fails WCAG AA and the
   * locked CONTRAST rule). This lighter step of the same hue holds ≥4.5:1
   * on every dark surface in use: 5.51 on bg #181D1F, 5.76 on dark
   * #171818, 4.84 on n800 #22282A, 4.55 on sectionLight #242D30.
   * Use for eyebrows, accent headline spans, stat values, and inline
   * links on dark; keep mkt.accent for fills (with white labels). */
  accentOnDark: '#6E8BFF',
  accentTint: 'rgba(13,60,252,0.10)',
  accentGlow: 'rgba(13,60,252,0.20)',
  dark: colors.effortel.n900,
  darkHover: colors.effortel.n800,
  onDark: colors.effortel.n100,
  onDarkMuted: colors.effortel.n400,
  /* See textFaint above — same fix. Was n600 #5F6F77 (~3.3:1, fails AA). */
  onDarkFaint: '#96A1A6',
  onDarkBorder: 'rgba(255,255,255,0.08)',
  overlay: 'rgba(255,255,255,0.05)',
  frost: 'rgba(34,40,42,0.85)',
  navHover: 'rgba(255,255,255,0.06)',
  success: colors.status.success,
  danger: colors.status.danger,
  warning: colors.status.warning,
  orange: '#F7B430',
  orangeTint: 'rgba(247,180,48,0.10)',
  cyan: colors.effortel.a500,
  cyanTint: 'rgba(13,60,252,0.10)',
  cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: 'rgba(255,255,255,0.08)',
  /* PRIMARY BUTTON — DOSS pattern (Sprint: marketing-polish).
   * Warm off-white cream button. Replaces the previous blue primary CTA.
   * The old buttonBg/Text/HoverBg fields below are deprecated and kept as
   * aliases of the cream tokens for backwards-compat during migration —
   * existing callers see the new cream button automatically; new code
   * should reference ctaBg / ctaText / ctaBgHover directly. */
  ctaBg: '#E6E3E0',
  ctaBgHover: '#D4CFC9',
  ctaText: '#1E1E1E',
  /* Secondary (ghost/outline) — used for "See demo" / "Learn more" buttons */
  ctaSecondaryBgHover: 'rgba(230,227,224,0.06)',
  ctaSecondaryBorder: 'rgba(224,220,216,0.5)',
  ctaSecondaryBorderHover: 'rgba(224,220,216,0.9)',
  ctaSecondaryText: '#F9F9F9',

  /* Deprecated — old blue button tokens. Now alias to the cream button
   * so anywhere that still reads buttonBg/Text/HoverBg picks up the new
   * design without a code change. Will be removed once all callers are
   * migrated to ctaBg/Text/Hover. Blue is still available via
   * mkt.accent / mkt.accentHover for decorative use. */
  buttonBg: '#E6E3E0',
  buttonText: '#1E1E1E',
  buttonHoverBg: '#D4CFC9',

  /* Dark canvas elevation layers (DOSS pattern).
   * Maintained alongside legacy bg/surface/sectionLight/sectionLighter for
   * gradual migration. New code should prefer these. */
  bgBase: '#161616',        // page bg — true near-black, not warmed gray
  bgDeeper: '#111111',      // deepest sections, hero backgrounds
  bgElevated: '#1C1C1C',    // cards, dashboard tiles
  bgHigher: '#242424',      // hover states, dropdowns, modal surfaces
  bgOverlay: 'rgba(22,22,22,0.92)',

  /* Hairline border scale. Was previously ad-hoc rgba(*,*,*,0.04…0.12).
   * These three values cover every existing case + the DOSS pattern. */
  hairline: 'rgba(133,128,123,0.18)',
  hairlineStrong: 'rgba(133,128,123,0.32)',
  hairlineDashed: 'rgba(133,128,123,0.08)',
  focusRing: 'rgba(13,60,252,0.5)',

  /* Text hierarchy aligned to DOSS — warm grays, never pure white/black.
   * Existing onDark/onDarkMuted/onDarkFaint kept for compat; these are
   * the canonical names going forward. */
  fg: '#F9F9F9',
  fgSecondary: '#A39E99',
  /* Warm tertiary meta text on dark. Old #78736E read ~3.0–3.9:1 on the DOSS
   * dark canvas (fails AA where used for readable meta). Raised to clear
   * 4.5:1 on every DOSS dark surface: 5.73 on bg, 6.09 on bgBase #161616,
   * 5.74 on bgElevated #1C1C1C, 5.23 on bgHigher #242424. Sits just below
   * fgSecondary (#A39E99) so the warm hierarchy holds. */
  fgTertiary: '#9A958F',
  /* fgDisabled intentionally left dim — disabled controls are WCAG-exempt and
   * must read as disabled, not as active secondary text. */
  fgDisabled: '#6B6662',

  darkBg: '#0d1514',
  lightBg: '#dfe8e6',
  /** Lighter section background — visible contrast against mkt.bg */
  sectionLight: '#242d30',
  /** Even lighter — for alternating sections that need clear pop */
  sectionLighter: '#2e393c',

  /* Warm cream "canvas" palette — W-HERO (Alex variant 04).
   * Scoped to the homepage hero only. Other surfaces stay on the dark
   * DOSS palette. Headline uses rich black (slate-950 — `onWarm`), sub
   * uses slate-600 (`onWarmMuted`), captions use slate-500
   * (`onWarmFaint`). Hairlines are slate-blue at 0.10 opacity. */
  warmCanvas: '#F3EDDF',
  warmCanvasMuted: '#EFE7D8',
  onWarm: '#0F172A',
  onWarmMuted: '#475569',
  onWarmFaint: '#64748B',
  warmHairline: 'rgba(71,85,105,0.12)',
  warmHairlineStrong: 'rgba(15,23,42,0.18)',
} as const;

export const tokens = {
  colors,
  typography,
  radius,
  shadows,
  transitions,
  mkt,
} as const;

export type Tokens = typeof tokens;
